import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Link } from 'wouter'
import { FileText, Share2, Eye, MessageSquare, Plus, Download, Clock, DollarSign, TrendingUp, Send } from 'lucide-react'
import { useEffect, useState } from 'react'

import toast from 'react-hot-toast'
import { generateRfpPdfSimple, fetchRfpDataForPdf } from '@/lib/pdf-utils'
import { buildApiUrl } from '@/config/api'

interface RfpStats {
  total: number
  published: number
  draft: number
  closed: number
  totalViews: number
  totalShares: number
  totalResponses: number
}

interface SupplierStats {
  totalResponses: number
  acceptedResponses: number
  pendingResponses: number
  reviewedResponses: number
  rejectedResponses: number
  withdrawnResponses: number
  totalBudget: number
  avgResponseTime: number
  successRate: number
}

interface Rfp {
  id: string
  title: string
  status: string
  views: number
  responses: number
  linkedinShares: number
  createdAt: string
}

interface Response {
  id: string
  status: string
  budget: number
  createdAt: string
  rfp: {
    id: string
    title: string
    status: string
    category: string
    budgetMin: number
    budgetMax: number
    budgetCurrency: string
    deadline: string
    user: {
      id: string
      name: string
      company: string
    }
  }
}

export function Dashboard() {

  const [stats, setStats] = useState<RfpStats>({
    total: 0,
    published: 0,
    draft: 0,
    closed: 0,
    totalViews: 0,
    totalShares: 0,
    totalResponses: 0,
  })
  const [supplierStats, setSupplierStats] = useState<SupplierStats>({
    totalResponses: 0,
    acceptedResponses: 0,
    pendingResponses: 0,
    reviewedResponses: 0,
    rejectedResponses: 0,
    withdrawnResponses: 0,
    totalBudget: 0,
    avgResponseTime: 0,
    successRate: 0,
  })
  const [recentRfps, setRecentRfps] = useState<Rfp[]>([])
  const [recentResponses, setRecentResponses] = useState<Response[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [publishingRfpId, setPublishingRfpId] = useState<string | null>(null)

  const exportRfpToPdf = async (rfp: Rfp) => {
    try {
      // Fetch complete RFP data from backend
      const pdfData = await fetchRfpDataForPdf(rfp.id)
      
      // Generate PDF with complete data
      generateRfpPdfSimple(pdfData)
      toast.success('PDF exported successfully!')
    } catch (error) {
      console.error('Error exporting PDF:', error)
      toast.error('Failed to export PDF')
    }
  }

  const publishRfp = async (rfpId: string) => {
    setPublishingRfpId(rfpId)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to publish your RFP')
        return
      }

      const response = await fetch(buildApiUrl(`/api/rfps/${rfpId}/publish`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        // Update the RFP status in the local state
        setRecentRfps(prev => prev.map(rfp => 
          rfp.id === rfpId ? { ...rfp, status: 'PUBLISHED' } : rfp
        ))
        toast.success('RFP published successfully!')
      } else {
        const errorData = await response.json()
        if (response.status === 400) {
          toast.error(errorData.error || 'Only draft RFPs can be published')
        } else if (response.status === 404) {
          toast.error('RFP not found')
        } else if (response.status === 401) {
          toast.error('Please log in to publish your RFP')
        } else {
          toast.error(errorData.error || 'Failed to publish RFP')
        }
      }
    } catch (error) {
      console.error('Error publishing RFP:', error)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        toast.error('Unable to connect to the server. Please check your internet connection and try again.')
      } else {
        toast.error('An unexpected error occurred. Please try again.')
      }
    } finally {
      setPublishingRfpId(null)
    }
  }

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          toast.error('Please log in to view your dashboard')
          return
        }

        let hasErrors = false

        // Fetch vendor stats
        try {
          const statsResponse = await fetch(buildApiUrl('/api/rfps/stats/overview'), {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })

          if (statsResponse.ok) {
            const statsData = await statsResponse.json()
            setStats(statsData.data)
          } else {
            console.error('Failed to fetch vendor stats:', statsResponse.status)
            hasErrors = true
          }
        } catch (error) {
          console.error('Error fetching vendor stats:', error)
          hasErrors = true
        }

        // Fetch supplier stats
        try {
          const supplierStatsResponse = await fetch(buildApiUrl('/api/responses/stats/supplier'), {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })

          if (supplierStatsResponse.ok) {
            const supplierStatsData = await supplierStatsResponse.json()
            setSupplierStats(supplierStatsData.data)
          } else {
            console.error('Failed to fetch supplier stats:', supplierStatsResponse.status)
            hasErrors = true
          }
        } catch (error) {
          console.error('Error fetching supplier stats:', error)
          hasErrors = true
        }

        // Fetch recent RFPs (user's own RFPs only)
        try {
          const rfpsResponse = await fetch(buildApiUrl('/api/rfps?limit=5&myRfps=true'), {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })

          if (rfpsResponse.ok) {
            const rfpsData = await rfpsResponse.json()
            setRecentRfps(rfpsData.data.slice(0, 5)) // Get latest 5 RFPs
          } else {
            console.error('Failed to fetch recent RFPs:', rfpsResponse.status)
            hasErrors = true
          }
        } catch (error) {
          console.error('Error fetching recent RFPs:', error)
          hasErrors = true
        }

        // Fetch recent responses (user's responses to RFPs)
        try {
          const responsesResponse = await fetch(buildApiUrl('/api/responses/recent/supplier?limit=5'), {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })

          if (responsesResponse.ok) {
            const responsesData = await responsesResponse.json()
            setRecentResponses(responsesData.data)
          } else {
            console.error('Failed to fetch recent responses:', responsesResponse.status)
            hasErrors = true
          }
        } catch (error) {
          console.error('Error fetching recent responses:', error)
          hasErrors = true
        }

        // Only show error if there were actual network/API errors
        if (hasErrors) {
          toast.error('Some dashboard data could not be loaded. Please try refreshing the page.')
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
        toast.error('Failed to load dashboard data. Please check your connection and try again.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">
              Welcome back! Here's an overview of your RFP activity.
            </p>
          </div>
          <Link href="/create">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create RFP
            </Button>
          </Link>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back! Here's an overview of your RFP activity.
          </p>
        </div>
        <div className="flex space-x-2">
          <Link href="/create">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create RFP
            </Button>
          </Link>
        </div>
      </div>

      {/* Vendor Stats Grid */}
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-4">Vendor Activity</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total RFPs</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.published} published, {stats.draft} drafts
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Views</CardTitle>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalViews.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  +12% from last month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">LinkedIn Shares</CardTitle>
                <Share2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalShares}</div>
                <p className="text-xs text-muted-foreground">
                  +8% from last month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Responses</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalResponses}</div>
                <p className="text-xs text-muted-foreground">
                  +15% from last month
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Supplier Stats Grid */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Supplier Activity</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Responses</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{supplierStats.totalResponses}</div>
                <p className="text-xs text-muted-foreground">
                  {supplierStats.acceptedResponses} accepted, {supplierStats.pendingResponses} pending
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{supplierStats.successRate}%</div>
                <p className="text-xs text-muted-foreground">
                  {supplierStats.acceptedResponses} accepted responses
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Budget</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${supplierStats.totalBudget.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  Across all responses
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{supplierStats.avgResponseTime}d</div>
                <p className="text-xs text-muted-foreground">
                  Days after RFP published
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent RFPs */}
        <Card>
          <CardHeader>
            <CardTitle>Recent RFPs</CardTitle>
            <CardDescription>
              Your latest RFP activity and performance metrics.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentRfps.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No RFPs yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first RFP to get started with your procurement process.
                </p>
                <Link href="/create">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First RFP
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {recentRfps.map((rfp) => (
                  <div
                    key={rfp.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="h-10 w-10 rounded-lg gradient-bg flex items-center justify-center">
                        <FileText className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <Link href={`/rfp/${rfp.id}`} className="hover:underline">
                          <h3 className="font-medium text-primary hover:text-primary/80 transition-colors">
                            {rfp.title}
                          </h3>
                        </Link>
                        <p className="text-sm text-muted-foreground">
                          Created {new Date(rfp.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-sm font-medium">{rfp.views} views</div>
                        <div className="text-sm text-muted-foreground">
                          {rfp.responses} responses
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        rfp.status === 'PUBLISHED' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : rfp.status === 'DRAFT'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {rfp.status.toLowerCase()}
                      </div>
                      {rfp.status === 'DRAFT' && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => publishRfp(rfp.id)}
                          disabled={publishingRfpId === rfp.id}
                          className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 h-8 px-3"
                        >
                          {publishingRfpId === rfp.id ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                              Publishing...
                            </>
                          ) : (
                            <>
                              <Send className="mr-1 h-3 w-3" />
                              Publish
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => exportRfpToPdf(rfp)}
                        className="h-8 w-8 p-0"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Responses */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Responses</CardTitle>
            <CardDescription>
              Your latest responses to RFPs and their status.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentResponses.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No responses yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start responding to RFPs to see your activity here.
                </p>
                <Link href="/all-rfps">
                  <Button>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Browse RFPs
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {recentResponses.map((response) => (
                  <div
                    key={response.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="h-10 w-10 rounded-lg gradient-bg flex items-center justify-center">
                        <MessageSquare className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <Link href={`/rfp/${response.rfp.id}`} className="hover:underline">
                          <h3 className="font-medium text-primary hover:text-primary/80 transition-colors">
                            {response.rfp.title}
                          </h3>
                        </Link>
                        <p className="text-sm text-muted-foreground">
                          {response.rfp.user.company} • ${response.budget.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-sm font-medium">${response.budget.toLocaleString()}</div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(response.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        response.status === 'ACCEPTED' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : response.status === 'PENDING'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : response.status === 'REVIEWED'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : response.status === 'REJECTED'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {response.status.toLowerCase()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
} 